import type * as React from "react";

import { cn, formatNumber } from "@homeinstead/ui";
import { forwardRef, memo } from "react";
import { HalfStar, Star } from "~care/components/ui/Icons";

function roundToNearestTenth(value: number): number {
  return Math.round(value * 10) / 10;
}

interface RatingProps extends React.HTMLAttributes<HTMLDivElement> {
  rating: number;
  ratingCount: number;
  service?: string;
}

/**
 * Rating component to display the rating and rating count.
 * The algorithm for showing the stars was inspired by Google and Amazon.
 */
const Rating = memo(
  forwardRef<HTMLDivElement, RatingProps>(
    ({ rating, ratingCount, service = "Google", className, ...props }, ref) => {
      const roundedRating = Math.max(
        0,
        Math.min(5, roundToNearestTenth(rating ?? 0)),
      );

      const formattedCount = formatNumber(ratingCount);

      return (
        <div
          className={cn("flex flex-row items-center gap-1", className)}
          {...props}
          aria-label={`Rating: ${roundedRating} out of 5 on ${service} with ${formattedCount} reviews`}
          ref={ref}
        >
          <RatingStars rating={roundedRating} />
          <div className="text-sm">
            {roundedRating} star rating on {service} ({formattedCount} reviews)
          </div>
        </div>
      );
    },
  ),
);

Rating.displayName = "Rating";

/**
 * RatingStars component to display the stars based on the rating.
 */
const RatingStars = memo(({ rating }: { rating: number }) => {
  const roundedRating = roundToNearestTenth(rating);
  const decimal = roundToNearestTenth(
    roundedRating - Math.floor(roundedRating),
  );
  const fullStars = Math.floor(roundedRating) + (decimal >= 0.8 ? 1 : 0);
  const halfStars = decimal >= 0.3 && decimal < 0.8 ? 1 : 0;
  const emptyStars = 5 - fullStars - halfStars;

  return (
    <div
      aria-label={`Rating: ${rating} out of 5`}
      className="flex flex-row items-center"
      role="img"
    >
      {Array.from({ length: fullStars }, (_, i) => (
        <Star key={`full-${i}`} variant="full" />
      ))}
      {halfStars === 1 && <HalfStar />}
      {Array.from({ length: emptyStars }, (_, i) => (
        <Star key={`empty-${i}`} variant="empty" />
      ))}
    </div>
  );
});
RatingStars.displayName = "RatingStars";

export { Rating, RatingStars };
